<template>
  <div class="game-table-flush">
  <vue-good-table 
    styleClass="vgt-table condensed vgt-flush"
    :key="showInterest ? 'tx-all' : 'tx-no-int'"
    v-bind="tableOptions"
    :columns="columns"
    :rows="visibleRows"
    :search-options="searchOptions"
    :pagination-options="paginationOptions"
    :row-click="true"
    @row-click="onRowClick"
    compactMode>
    <template #table-row="props">
      <div v-if="props.column.field === 'ISINPrc'" class="d-flex flex-column align-items-end">
        <span>{{ props.row.ISINPrc != null && props.row.ISINPrc !== '' ? formatFn(props.row.ISINPrc) : '' }}</span>
        <span v-if="showAvgPrice(props.row)" class="text-xs text-muted">
          prům. {{ formatFn(props.row.ISINAvgPrc) }}
        </span>
      </div>
      <span
        v-else-if="props.column.field === 'ISINAmt'"
        class="num-cell"
      >
        <template v-if="hasQuantity(props.row.ISINAmt)">
          {{ props.formattedRow[props.column.field] }}<span class="qty-ks">ks</span>
        </template>
      </span>
      <span
        v-else-if="props.column.field === 'Amt'"
        class="num-cell"
        :class="changeClass(props.row.Amt)"
      >
        {{ props.formattedRow[props.column.field] }}
      </span>
      <span v-else>{{ props.formattedRow[props.column.field] }}</span>
    </template>
    <template #emptystate>
      <div class="text-center">
        <span>Žádná data</span>
      </div>
    </template>
  </vue-good-table>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { tableOptions, tableSearchOptions, paginationFor } from '../../../../helpers/tableOptions';

const props = defineProps({
  rows: Array,
  showInterest: {
    type: Boolean,
    default: false
  }
});

const store = useStore();

const searchOptions = {
  ...tableSearchOptions,
  placeholder: 'Hledat transakci…'
};

const isInterest = (row) => {
  const typ = String(row?.Typ || row?.CapitalType || '').toUpperCase();
  if (typ === 'LT_I') return true;
  return /^\s*úrok/i.test(String(row?.Dsc || ''));
};

const visibleRows = computed(() => {
  const list = Array.isArray(props.rows) ? props.rows : [];
  if (props.showInterest) return list;
  return list.filter((row) => !isInterest(row));
});

const paginationOptions = computed(() => ({
  ...paginationFor(visibleRows.value),
  perPage: props.showInterest ? 25 : 15
}));

const formatFn = (value) => {
  return store.getters.formatTableNumber(value);
};

const formatChangeTypeNumber = (value) => {
  return store.getters.formatChangeTypeNumber(value);
}

const changeClass = (value) => {
  const n = Number(value);
  if (Number.isNaN(n) || n === 0) return '';
  return n > 0 ? 'text-success' : 'text-danger';
}

const parsePrice = (value) => {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/\u00a0/g, '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const showAvgPrice = (row) => {
  const avg = parsePrice(row?.ISINAvgPrc);
  if (avg == null) return false;
  const prc = parsePrice(row?.ISINPrc);
  if (prc == null) return true;
  return avg !== prc;
};

const hasQuantity = (value) => {
  if (value == null || value === '') return false;
  const n = Number(String(value).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n);
}

const onRowClick = (params) => {
  if (params.row.ISINID) {
    store.dispatch('fetchISINChartData', params.row);
  }
}

const columns = reactive([
  { 
    label: 'Datum',
    field: 'Dt',
    type: 'date',
    dateInputFormat: 'dd.MM.yyyy',
    dateOutputFormat: 'dd.MM.yyyy',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold',
  },
  { 
    label: 'Popis transakce',
    field: 'Dsc',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold',
  },
  { 
    label: 'Množství',
    field: 'ISINAmt',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
  },
  { 
    label: 'Aktivum',
    field: 'Name',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold',
  },
  {
    label: 'Cena',
    field: 'ISINPrc',
    type: 'number',
    tooltip: 'Cena pokynu a průměrná nákupní cena',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
    formatFn: formatFn
  },
  { 
    label: 'Obrat',
    field: 'Amt',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end',
    formatFn: formatChangeTypeNumber
  }
]);

</script>

<style scoped>
.game-table-flush :deep(.vgt-table td) {
  padding-top: 0.35rem !important;
  padding-bottom: 0.35rem !important;
}

.game-table-flush :deep(.vgt-wrap__footer) {
  background: transparent;
  border: none;
  padding: 0.5rem 0.25rem 0.15rem;
}

.game-table-flush :deep(.vgt-responsive) {
  margin-bottom: 0 !important;
}

.qty-ks {
  margin-left: 0.22em;
  font-weight: 600;
  font-size: 0.82em;
  opacity: 0.75;
}

.num-cell {
  display: block;
  width: 100%;
  text-align: right;
  white-space: nowrap;
}
</style>
